/**
 * Utility functions for formatting markdown links in AI responses
 */
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';

/**
 * Convert markdown links to HTML links
 * Handles patterns like [text](url) and converts them to <a> tags
 */
export const formatMarkdownLinks = (content: string): string => {
  // Match markdown links [text](url), but leave markdown images ![alt](url) alone.
  const markdownLinkRegex = /(^|[^!])\[([^\]]+)\]\(([^)]+)\)/g;

  return content.replace(markdownLinkRegex, (match, prefix, text, url) => {
    // Validate URL to ensure it's safe
    try {
      const urlObj = new URL(url, window.location.href);
      // Allow http(s) and app-relative links.
      if (urlObj.protocol === 'http:' || urlObj.protocol === 'https:') {
        const isAhriDetailsLink = isAhriDetailsUrl(urlObj);
        const linkText = isAhriDetailsLink ? ahriLinkLabel(text) : text;
        const className = isAhriDetailsLink ? 'markdown-link ahri-link' : 'markdown-link';
        return `${prefix}<a href="${url}" target="_blank" rel="noopener noreferrer" class="${className}">${linkText}</a>`;
      }
    } catch (error) {
      // If URL parsing fails, return the original match
      console.warn('Invalid URL in markdown link:', url);
      return match;
    }

    return match;
  });
};

const escapeHtmlAttribute = (value: string): string => {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
};

const isAhriDetailsUrl = (url: URL): boolean => {
  const host = url.hostname.toLowerCase().replace(/^www\./, '');
  return host === 'ahridirectory.org' && /^\/details\/\d+\/\d+\/?$/.test(url.pathname);
};

const ahriLinkLabel = (label: string): string => {
  const cleanLabel = String(label || '').trim();
  if (!cleanLabel || /^https?:\/\//i.test(cleanLabel) || /ahridirectory\.org\/details/i.test(cleanLabel)) {
    return 'Open AHRI certificate page';
  }
  return cleanLabel;
};

const isPrivateIpv4Host = (host: string): boolean => {
  const match = host.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (!match) {
    return false;
  }
  const octets = match.slice(1).map(Number);
  if (octets.some((octet) => Number.isNaN(octet) || octet < 0 || octet > 255)) {
    return false;
  }
  return (
    octets[0] === 10 ||
    octets[0] === 127 ||
    (octets[0] === 172 && octets[1] >= 16 && octets[1] <= 31) ||
    (octets[0] === 192 && octets[1] === 168)
  );
};

const isSafePublicTextUrl = (url: URL): boolean => {
  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return false;
  }

  const host = url.hostname.toLowerCase().replace(/^www\./, '');
  if (
    host === 'localhost' ||
    host === 'smartdocs_agent_v2' ||
    host === 'smartdocs-agent-v2' ||
    host === 'smartdocs_backend' ||
    host === 'smartdocs-backend' ||
    host === 'api.work-orders' ||
    host.endsWith('.local') ||
    host.endsWith('.internal') ||
    host.endsWith('.docker') ||
    host.includes('digitaloceanspaces.com') ||
    isPrivateIpv4Host(host)
  ) {
    return false;
  }

  return true;
};

const labelForPublicTextUrl = (url: URL): string => {
  if (isAhriDetailsUrl(url)) {
    return 'Open AHRI certificate page';
  }

  const host = url.hostname.replace(/^www\./i, '');
  const pathName = decodeURIComponent(url.pathname || '').split('/').filter(Boolean).pop() || '';
  const cleanPathName = pathName.replace(/\.[a-z0-9]{2,5}$/i, '').replace(/[-_]+/g, ' ').trim();
  return cleanPathName ? `Open ${host}: ${cleanPathName}` : `Open ${host}`;
};

export const resolveSmartDocsOpenUrl = (url: string): string => {
  const trimmedUrl = String(url || '').trim();
  if (/^\/api\//i.test(trimmedUrl)) {
    const env = import.meta.env as Record<string, string | undefined>;
    const configuredApiBase = env.VITE_SMARTDOCS_API_BASE_URL?.trim();
    const currentOrigin = typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : '';
    const localBackendOrigin = currentOrigin.replace(/:3000$/, ':5002');
    const backendBase = (configuredApiBase || localBackendOrigin || currentOrigin).replace(/\/api\/v1\/?$/, '');
    return `${backendBase.replace(/\/$/, '')}${trimmedUrl}`;
  }
  return trimmedUrl;
};

/**
 * Check if a string contains markdown links
 */
export const hasMarkdownLinks = (content: string): boolean => {
  const markdownLinkRegex = /(^|[^!])\[([^\]]+)\]\(([^)]+)\)/g;
  return markdownLinkRegex.test(content);
};

/**
 * Extract all markdown links from content
 */
export const extractMarkdownLinks = (content: string): Array<{text: string, url: string}> => {
  const markdownLinkRegex = /(^|[^!])\[([^\]]+)\]\(([^)]+)\)/g;
  const links: Array<{text: string, url: string}> = [];
  let match;

  while ((match = markdownLinkRegex.exec(content)) !== null) {
    links.push({
      text: match[2],
      url: match[3]
    });
  }

  return links;
};

/**
 * Clean up malformed HTML that might be generated by AI
 */
const cleanMalformedHtml = (content: string): string => {
  return content
    // Fix nested S3 links: <a href="<a href="https://nyc3.digitaloceanspaces.com/carrier/filename.pdf" class="s3-link" target="_blank" rel="noopener noreferrer">filename.pdf</a>" target="_blank" rel="noopener noreferrer">filename.pdf</a>
    .replace(/<a href="<a href="([^"]+)" class="s3-link" target="_blank" rel="noopener noreferrer">([^<]+)<\/a>" target="_blank" rel="noopener noreferrer">([^<]+)<\/a>/g, (match, url, text1, text2) => {
      return `<a href="${url}" class="s3-link" target="_blank" rel="noopener noreferrer">${text1}</a>`;
    })
    // Fix the specific pattern we're seeing: filename.pdf" class="s3-link" target="_blank" rel="noopener noreferrer">filename.pdf" target="_blank" rel="noopener noreferrer">filename.pdf
    .replace(/([^<>\s]+\.pdf)"\s+class="s3-link"\s+target="_blank"\s+rel="noopener noreferrer">([^<]+)"\s+target="_blank"\s+rel="noopener noreferrer">([^<]+)/g, (match, filename, text1, text2) => {
      // Extract the clean filename and create a proper link
      const cleanFilename = filename.replace(/^.*\//, ''); // Remove any path prefix
      const cleanUrl = `https://nyc3.digitaloceanspaces.com/carrier/${cleanFilename}`;
      return `<a href="${cleanUrl}" class="s3-link" target="_blank" rel="noopener noreferrer">${cleanFilename}</a>`;
    })
    // Fix other malformed patterns
    .replace(/([^<>\s]+\.pdf)"\s+class="s3-link"[^>]*>([^<]+)"\s+target="_blank"\s+rel="noopener noreferrer">([^<]+)/g, (match, filename, text1, text2) => {
      const cleanFilename = filename.replace(/^.*\//, '');
      const cleanUrl = `https://nyc3.digitaloceanspaces.com/carrier/${cleanFilename}`;
      return `<a href="${cleanUrl}" class="s3-link" target="_blank" rel="noopener noreferrer">${cleanFilename}</a>`;
    })
    // Clean up any remaining duplicate attributes
    .replace(/target="_blank"\s+rel="noopener noreferrer"\s+target="_blank"\s+rel="noopener noreferrer"/g, 'target="_blank" rel="noopener noreferrer"')
    // Fix broken closing tags
    .replace(/>([^<]+)"\s+target="_blank"\s+rel="noopener noreferrer">([^<]+)/g, '>$1');
};

/**
 * Format both S3 links and markdown links
 */
export const formatAllLinks = (content: string): string => {
  // First clean up any malformed HTML
  let formatted = cleanMalformedHtml(content);

  // Check if content already has S3 links formatted (avoid double-processing)
  // Also check for any existing HTML to avoid rewriting URLs inside attributes like <img src="...">.
  const hasExistingS3Links = /class="s3-link"/.test(formatted);
  const hasExistingLinks = /<a\s+[^>]*href\s*=/i.test(formatted);
  const hasHtmlTags = /<\s*[a-z][^>]*>/i.test(formatted);

  if (!hasExistingS3Links && !hasExistingLinks && !hasHtmlTags) {
    // Convert raw SmartDocs work-order URLs into clickable links. The agent
    // sometimes emits "PDF: /api/work-orders/pdf?..." instead of markdown.
    const workOrderUrlRegex = new RegExp(
      String.raw`(^|\s)(?:PDF:\s*)?((?:https?:\/\/api\.work-orders\/pdf\?[^\s<>)"']+)|(?:(?:https?:\/\/[^/\s<>)"']+)?\/api\/work-orders\/pdf\?[^\s<>)"']+)|(?:(?:https?:\/\/[^/\s<>)"']+)?\/api\/work-orders\/\d+\/pdf(?:\?[^\s<>)"']+)?))`,
      'gi',
    );
    formatted = formatted.replace(workOrderUrlRegex, (_match, prefix, url) => {
      const label = /signed/i.test(url) ? 'Open signed work order' : 'Open work order';
      return `${prefix}[${label}](${url})`;
    });

    // Convert raw AHRI details URLs into a clear contractor-facing CTA.
    const ahriDetailsUrlRegex = /(^|\s)(https?:\/\/(?:www\.)?ahridirectory\.org\/details\/\d+\/\d+\/?)(?=\s|$|>|"|'|\))/gi;
    formatted = formatted.replace(ahriDetailsUrlRegex, (_match, prefix, url) => {
      return `${prefix}[Open AHRI certificate page](${url})`;
    });

    // Convert normal public web URLs into clickable markdown links. Keep this
    // conservative so private artifacts, backend routes, and container hosts
    // never become app-facing links by accident.
    const publicTextUrlRegex = /(^|\s)(https?:\/\/[^\s<>"']+?)([.,;:!?)]?)(?=\s|$|>|"|'|\))/gi;
    formatted = formatted.replace(publicTextUrlRegex, (match, prefix, rawUrl, punctuation) => {
      try {
        const url = new URL(rawUrl);
        if (!isSafePublicTextUrl(url)) {
          return match;
        }
        return `${prefix}[${labelForPublicTextUrl(url)}](${url.toString()})${punctuation || ''}`;
      } catch (_error) {
        return match;
      }
    });

    // Format S3 links (DigitalOcean Spaces) only for plain-text responses.
    const s3UrlRegex = /https:\/\/nyc3\.digitaloceanspaces\.com\/([^/\s]+)\/([^\s<>"']+?)(?:\?[^\s<>"']*)?(?=\s|$|>|"|'|\))/g;
    formatted = formatted.replace(s3UrlRegex, (match, bucket, filename) => {
      try {
        const url = new URL(match);
        const pathParts = url.pathname.split('/').filter(part => part.length > 0);

        if (pathParts.length < 2) {
          return match;
        }

        const actualBucket = pathParts[0];
        const actualFilename = pathParts.slice(1).join('/');
        const displayName = actualFilename.split('/').pop() || actualFilename;

        return `<a href="${escapeHtmlAttribute(url.toString())}" class="s3-link" target="_blank" rel="noopener noreferrer">${displayName}</a>`;
      } catch (error) {
        console.error('[MarkdownLinkFormatter] Error processing S3 URL:', error);
        return match;
      }
    });
  }

  // Then format markdown links
  formatted = formatMarkdownLinks(formatted);

  return formatted;
};

/**
 * Open URL using Capacitor Browser or fallback to window.open
 */
export const openUrl = async (url: string): Promise<void> => {
  try {
    const resolvedUrl = resolveSmartDocsOpenUrl(url);
    // Validate URL before opening
    if (!resolvedUrl || typeof resolvedUrl !== 'string') {
      console.error('[MarkdownLinkFormatter] Invalid URL provided:', url);
      return;
    }

    // Check if URL contains HTML tags (which would cause the error we saw)
    if (resolvedUrl.includes('<') || resolvedUrl.includes('>') || resolvedUrl.includes('%3C') || resolvedUrl.includes('%3E')) {
      console.error('[MarkdownLinkFormatter] URL contains HTML tags, this is invalid:', resolvedUrl);
      return;
    }

    if (Capacitor.isNativePlatform()) {
      await Browser.open({ url: resolvedUrl });
    } else {
      window.open(resolvedUrl, '_blank');
    }
  } catch (error) {
    console.error('[MarkdownLinkFormatter] Error opening URL:', error);
    // Final fallback to window.open
    window.open(resolveSmartDocsOpenUrl(url), '_blank');
  }
};

/**
 * Initialize markdown link handlers
 * This should be called after the DOM is updated with formatted content
 */
export const initializeMarkdownLinkHandlers = (): void => {
  const markdownLinks = document.querySelectorAll('a.markdown-link');
  if (markdownLinks.length === 0) {
    return;
  }

  markdownLinks.forEach((link) => {
    link.removeEventListener('click', handleMarkdownLinkClick);
  });

  markdownLinks.forEach((link) => {
    const href = link.getAttribute('href');
    if (!href || href.includes('<') || href.includes('>')) {
      console.error('[MarkdownLinkFormatter] Invalid href on markdown link:', href);
      return;
    }

    link.addEventListener('click', handleMarkdownLinkClick);
  });
};

/**
 * Handle markdown link clicks
 */
const handleMarkdownLinkClick = async (event: Event): Promise<void> => {
  const link = event.target as HTMLAnchorElement;

  if (!link || link.tagName !== 'A') {
    return;
  }

  // Prevent default navigation
  event.preventDefault();
  event.stopPropagation();

  // Get the URL from the href attribute, not the innerHTML
  const url = link.getAttribute('href');

  if (!url) {
    console.error('[MarkdownLinkFormatter] No href attribute found on link');
    return;
  }

  try {
    await openUrl(url);
  } catch (error) {
    console.error('[MarkdownLinkFormatter] Error opening URL:', error);
  }
};
